import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
} from '@mui/material';
import api from '../../utils/api';

export default function HelpDeskNewTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const [severities, setSeverities] = useState([]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [severityId, setSeverityId] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/helpdesk/severities')
      .then((r) => {
        const list = r.data?.data || [];
        setSeverities(list);
        if (list[0]?.id) setSeverityId(list[0].id);
      })
      .catch(() => setSeverities([]));
  }, []);

  useEffect(() => {
    const draft = location.state?.ticketDraft;
    if (!draft || typeof draft !== 'object') return;
    if (typeof draft.subject === 'string' && draft.subject.trim()) {
      setSubject((s) => (s.trim() ? s : draft.subject));
    }
    if (typeof draft.description === 'string') {
      setDescription((d) => (d.trim() ? d : draft.description));
    }
  }, [location.state]);

  const formatEta = (m) => {
    if (m == null) return '';
    if (m < 60) return `${m} min`;
    if (m < 1440) return `${Math.round(m / 60)} hr(s)`;
    if (m < 10080) return `${Math.round(m / 1440)} day(s)`;
    return `${Math.round(m / 10080)} wk(s)`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!severityId) {
      setError('Severity is required.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject.trim());
      fd.append('description', description);
      fd.append('severity_id', severityId);
      files.forEach((f) => fd.append('attachments', f));
      const r = await api.post('/helpdesk/tickets', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const id = r.data?.data?.id;
      if (id) navigate(`/helpdesk/${id}`);
      else navigate('/helpdesk/my');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720 }}>
      <Typography variant="h5" gutterBottom>
        New help desk ticket
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reference format: TKT-XXXX-XXXX-XXXX. Attachments: PNG, JPG, PDF, DOC, or DOCX only (max 10 MB each).
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2 }} component="form" onSubmit={submit}>
        <TextField
          label="Subject"
          fullWidth
          required
          margin="normal"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          select
          label="Severity"
          fullWidth
          required
          margin="normal"
          value={severityId}
          onChange={(e) => setSeverityId(e.target.value)}
        >
          {severities.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
              {s.turnaround_minutes != null
                ? ` (target ~ ${formatEta(s.turnaround_minutes)})`
                : ''}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" component="label" sx={{ mt: 2, mr: 2 }}>
          Attach files
          <input
            type="file"
            hidden
            multiple
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,image/png,image/jpeg,application/pdf"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </Button>
        {files.length > 0 && (
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {files.map((f) => f.name).join(', ')}
          </Typography>
        )}
        <Box sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Submitting…' : 'Submit ticket'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
